import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { USER_TYPE_CONFIG, REGISTERABLE_USER_TYPES } from '@/shared/types/users.types';

interface HeaderProps {
  isOfflineMode: boolean;
  setShowLanguageModal: (show: boolean) => void;
  setShowCommunityDelivery: (show: boolean) => void;
  setShowRealTimeTracking: (show: boolean) => void;
}

export default function Header({
  isOfflineMode,
  setShowLanguageModal,
  setShowCommunityDelivery,
}: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">

          {/* ── Logo ──────────────────────────────────────── */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center">
              <i className="ri-truck-line text-white text-base" />
            </div>
            <span
              className="text-2xl font-bold text-teal-600"
              style={{ fontFamily: 'Pacifico, serif' }}
            >
              ONAPILA
            </span>
            {isOfflineMode && (
              <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                <i className="ri-wifi-off-line mr-1" />Hors ligne
              </span>
            )}
          </Link>

          {/* ── Navigation desktop ───────────────────────── */}
          <nav className="hidden md:flex items-center gap-6">
            <button
              onClick={() => setShowCommunityDelivery(true)}
              className="text-gray-600 hover:text-teal-600 font-medium text-sm transition-colors"
            >
              Services
            </button>
            <Link to="/tracking" className="text-gray-600 hover:text-teal-600 font-medium text-sm transition-colors">
              Suivi colis
            </Link>
            <Link to="/register/livreur" className="text-gray-600 hover:text-teal-600 font-medium text-sm transition-colors">
              Devenir livreur
            </Link>
          </nav>

          {/* ── Actions desktop ──────────────────────────── */}
          <div className="hidden md:flex items-center gap-3">

            {/* Langue */}
            <button
              onClick={() => setShowLanguageModal(true)}
              className="flex items-center gap-1 text-gray-600 hover:text-teal-600 text-sm transition-colors px-2 py-1.5 rounded-lg hover:bg-gray-50"
            >
              <span className="text-base">🇫🇷</span>
              <span className="font-medium">FR</span>
              <i className="ri-arrow-down-s-line text-xs" />
            </button>

            {/* Connexion */}
            <Link
              to="/login"
              className="px-4 py-2 text-teal-700 border border-teal-300 rounded-lg text-sm font-medium hover:bg-teal-50 transition-colors"
            >
              <i className="ri-login-circle-line mr-1.5" />
              Connexion
            </Link>

            {/* S'inscrire — dropdown */}
            <div className="relative group">
              <button className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors flex items-center gap-1">
                <i className="ri-user-add-line" />
                S'inscrire
                <i className="ri-arrow-down-s-line text-xs" />
              </button>

              {/* Dropdown */}
              <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100
                opacity-0 invisible group-hover:opacity-100 group-hover:visible
                transition-all duration-200 z-50 py-2">

                {REGISTERABLE_USER_TYPES.map(type => {
                  const cfg = USER_TYPE_CONFIG[type];
                  if (!cfg) return null;
                  return (
                    <Link
                      key={type}
                      to={`/register/${type}`}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors group/item"
                    >
                      <div className={`w-8 h-8 bg-${cfg.color}-100 rounded-lg flex items-center justify-center shrink-0`}>
                        <i className={`${cfg.icon} text-${cfg.color}-600 text-sm`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{cfg.label}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[140px]">{cfg.description}</p>
                      </div>
                    </Link>
                  );
                })}

                <div className="border-t border-gray-100 mt-1 pt-1 px-4 pb-2">
                  <button
                    onClick={() => navigate('/register/client')}
                    className="w-full text-center text-xs text-teal-600 hover:text-teal-800 font-medium py-1"
                  >
                    Voir tous les types →
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── Burger mobile ────────────────────────────── */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setMenuOpen(o => !o)}
          >
            <i className={`text-xl text-gray-700 ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>
      </div>

      {/* ── Menu mobile ──────────────────────────────────── */}
      {menuOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white px-4 py-4 space-y-2">
          <Link to="/login"
            className="flex items-center gap-3 px-4 py-3 bg-teal-50 text-teal-700 rounded-xl font-semibold"
            onClick={() => setMenuOpen(false)}>
            <i className="ri-login-circle-line" />Se connecter
          </Link>

          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 pt-2">
            S'inscrire comme…
          </p>

          {REGISTERABLE_USER_TYPES.map(type => {
            const cfg = USER_TYPE_CONFIG[type];
            if (!cfg) return null;
            return (
              <Link
                key={type}
                to={`/register/${type}`}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className={`w-8 h-8 bg-${cfg.color}-100 rounded-lg flex items-center justify-center`}>
                  <i className={`${cfg.icon} text-${cfg.color}-600 text-sm`} />
                </div>
                <span className="text-sm font-medium text-gray-700">{cfg.label}</span>
              </Link>
            );
          })}

          <div className="border-t pt-2 mt-2 flex gap-2">
            <Link to="/tracking" onClick={() => setMenuOpen(false)}
              className="flex-1 text-center py-2.5 text-sm text-gray-600 hover:text-teal-600 font-medium">
              <i className="ri-map-pin-line mr-1" />Suivi
            </Link>
            <button onClick={() => { setShowCommunityDelivery(true); setMenuOpen(false); }}
              className="flex-1 text-center py-2.5 text-sm text-gray-600 hover:text-teal-600 font-medium">
              <i className="ri-community-line mr-1" />Services
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
